import type { Address } from '@frontend/api-types';
import { useCallback, useEffect, useRef, useState } from '@frontend/react';

import { useLazyAddressAutocompleteQuery, useLazyAddressDetailsQuery } from './api';

type AddressAutocompleteResult = {
  placeId: string;
  text: string;
};

type UseAddressAutocompleteResult = {
  clearSearch: () => void;
  isSearching: boolean;
  locationResults: ReadonlyArray<AddressAutocompleteResult>;
  searchDetails: (placeId: string) => Promise<Omit<Address, 'alias'> | null>;
  setTerm: (term: string) => void;
  term: string;
};

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

export const useAddressAutocomplete = (): UseAddressAutocompleteResult => {
  const [isSearching, setIsSearching] = useState(false);
  const [term, setTerm] = useState('');
  const [locationResults, setLocationResults] = useState<ReadonlyArray<AddressAutocompleteResult>>([]);
  const [getAutocompleteResults] = useLazyAddressAutocompleteQuery();
  const [getAddressDetails] = useLazyAddressDetailsQuery();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (term.length < MIN_SEARCH_LENGTH) {
      setLocationResults([]);
      return;
    }

    // eslint-disable-next-line fp/no-mutation -- Ref mutation for debounce timer
    timerRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await getAutocompleteResults(term);
        if (response.data) {
          setLocationResults(response.data);
        }
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [term, getAutocompleteResults]);

  const searchDetails = useCallback(
    async (placeId: string): Promise<Omit<Address, 'alias'> | null> => {
      const response = await getAddressDetails(placeId);
      return response.data ?? null;
    },
    [getAddressDetails],
  );

  const clearSearch = useCallback(() => {
    setLocationResults([]);
    setIsSearching(false);
  }, []);

  return {
    clearSearch,
    isSearching,
    locationResults,
    searchDetails,
    setTerm,
    term,
  };
};
